import { useMemo } from 'react';

import { company } from '@/config/company';
import { type IconKey } from '@/content/site';

/**
 * One figure in the story band's rail. A `value` of `null` means the fact is a
 * word, not a number, so the tile renders it as text rather than counting up
 * to it.
 */
export type CompanyFact = {
  icon: IconKey;
  /** The figure as it is shown. */
  display: string;
  /** The same figure as a number, when it is one, for the count-up. */
  value: number | null;
  label: string;
  note: string;
};

export type CompanyStory = {
  /** The narrative in full, for a band with a column to spend on it. */
  paragraphs: readonly string[];
  /**
   * The same company in two sentences, for a band a host page is lending a
   * slot to.
   *
   * It is written rather than sliced off `paragraphs` because a truncation
   * would read as one: the first full paragraph opens on the legal name and
   * both years, which the fact rail beside it already states, so a compact
   * band using it would say every number twice and introduce the company
   * nowhere. These two sentences lead on the span of time and the place, and
   * leave the numbers to the rail.
   */
  summary: readonly string[];
  facts: readonly CompanyFact[];
};

/**
 * The story band's words and figures, both derived from `config/company.ts`.
 *
 * The derivation is the reason this is a hook and not a constant: the prose
 * interpolates the legal name, the brand name, the address and the two years,
 * and the rail turns the same two years into countable numbers. Keeping that
 * in one place means no copy of the band can state a year its own rail
 * contradicts.
 *
 * It lives in `src/hooks` rather than beside the About page because the band
 * it feeds is now shared — six pages render it, so a hook reachable only
 * through `pages/about` would be the one piece of the band that the other five
 * had to import across a page boundary.
 *
 * Supplied facts only (§59): no invented counts, no awards, no staff numbers.
 */
export function useCompanyStory(): CompanyStory {
  return useMemo(() => {
    const { address } = company;

    return {
      paragraphs: [
        `${company.legalName} was established in ${company.established} and fully incorporated in ${company.incorporated}. We work with car dealers around the world to help customers in Uganda find, import and register the car they want.`,
        `Our office is on ${address.street}, in ${address.city}. Our team guides every customer from the first quotation through to clearing, registration and delivery — the same way, whether it is one executive car or a whole fleet.`,
      ],
      summary: [
        `${company.brandName} has been importing cars for customers in Uganda since ${company.established}, working with dealers in Japan, China, the UK and beyond.`,
        `Our office is on ${address.street} in ${address.city}, and the same team takes you from the first quotation through to clearing, registration and delivery.`,
      ],
      facts: [
        {
          icon: 'calendar',
          display: String(company.established),
          value: company.established,
          label: 'Established',
          note: 'Importing cars for customers in Uganda since.',
        },
        {
          icon: 'verified',
          display: String(company.incorporated),
          value: company.incorporated,
          label: 'Fully incorporated',
          note: `Registered as ${company.legalName}`,
        },
        {
          icon: 'location',
          display: address.city,
          value: null,
          label: `Head office, ${address.country}`,
          note: `${address.building}, ${address.street}`,
        },
      ],
    };
  }, []);
}
